<Modal
    {id}
    bind:enabled
    titleText={i18n.addResources}
    cancelText={i18n.cancel}
    confirmText={i18n.save}
    scrollBody
    on:close={(e) => onClose(e.detail)}
>
    <div slot="banner">
        <slot name="banner" />
    </div>
    <div slot="body" class="prom-resource-tree__body">
        <ul
            class="prom-resource-tree"
            role="tree"
            aria-multiselectable={true}
            aria-labelledby="{id}-header"
            tabindex="0"
            on:keydown={keyDownTree}
        >
            {#if !rootElement.childrenLoading && rootElement.totalChildren === 0}
                <li class="prom-resource-tree-item__empty">{i18n.folderEmptyState}</li>
            {:else}
                {#each rootResources as resource}
                    <ResourceTreeItem
                        {resource}
                        {i18n}
                        bind:selectedId
                        {resources}
                        on:loadChildren={(e) => loadChildren(e.detail)}
                    />
                {/each}

                {#if (rootElement.childrenLoaded < rootElement.totalChildren) || !rootElement.childrenLoaded}
                    <ScrollTrigger on:visible={() => loadChildren(rootElement)} />
                {/if}
            {/if}
        </ul>
    </div>
</Modal>

<script>
    import Modal from '../../Modal/v2/Modal.svelte';
    import ResourceTreeItem from './ResourceTreeItem.svelte';
    import ScrollTrigger from './ScrollTrigger.svelte';
    import { createEventDispatcher } from 'svelte';
    import { Keys } from '@itslearning/atlas/keyboard/keys';
    import { normalizeKey } from '@itslearning/atlas/keyboard/normalize';

    const dispatch = createEventDispatcher();

    /**
     * @typedef Resource
     * @type {object}
     * @property {number} id
     * @property {boolean} [checked]
     * @property {boolean} active
     * @property {number} parentId
     * @property {string} title
     * @property {string} type
     * @property {string} iconUrl
     * @property {boolean} [disabled]
     * @property {boolean} [childrenLoading]
     * @property {boolean} [childrenLoaded]
     * @property {boolean} [totalChildren]
     * @property {boolean} [pageIndex]
     */

    /** @type {string} Defaults to a random string */
    export let id = Math.random().toString(36).substr(2, 16);

    /** @type {boolean} */
    export let enabled = false;

    /** @type {Number} */
    export let rootId = 0;

    /** @type {boolean} */
    export let initialSelectionDisabled = false;

    /**
     * @callback getResources
     * @param {number} parentId - The parent id to return resources for
     * @param {number} pageIndex
     * @returns {Promise<{ resources: Resource[], totalCount: Number }>} Promise object resolving to an array of resources
     */

    /** @type {getResources} A method returning resources for a parent */
    export let getResources;

    /** @type { Resource[] }*/
    export let initialResources = [];

    /**
     * @typedef ResourcePickerTerms
     * @type {object}
     * @property {string} [addResources]
     * @property {string} [save]
     * @property {string} [cancel]
     * @property {string} [folderEmptyState]
     * @property {string} [expand]
     */

    /** @type {ResourcePickerTerms} i18n terms for the component */
    export let i18n = {};

    let resources = [];
    let selectedId = undefined;
    let rootElement = {};
    let rootResources = [];

    $: {
        rootResources = resources.filter(r => r.parentId === rootId);
    }

    $: if (enabled) {
        onEnable();
    }

    function keyDownTree(event) {
        const { key } = event;

        if (selectedId !== undefined && [Keys.SPACE, Keys.ENTER, Keys.DOWN].includes(normalizeKey(key))) {
            document.getElementById(`resource-tree-item-${selectedId}`).focus();
            event.preventDefault();
            event.stopPropagation();
        }
    }

    function onEnable() {
        resources = [];
        rootResources = [];
        rootElement = { id: rootId };

        loadChildren(rootElement);
    }

    async function loadChildren(resource) {
        if (resource.pageIndex === undefined) {
            resource.pageIndex = 0;
            resource.totalChildren = 0;
            resource.childrenLoaded = 0;
        } else if (resource.childrenLoaded >= resource.totalChildren) {
            return;
        }

        if (!resource.childrenLoading) {
            resource.childrenLoading = true;
            resources = [...resources];

            let { resources: newResources, totalCount } = await getResources(resource.id, resource.pageIndex);

            resource.totalChildren = totalCount;
            resource.childrenLoaded += newResources.length;
            resource.pageIndex += 1;

            newResources = newResources.map(newResource => {
                newResource.checked = initialResources.some(initial => initial.id === newResource.id);

                // If the initialSelectionDisabled setting is used then resources from the
                // initialResources are marked disabled and cannot be unchecked.
                if (initialSelectionDisabled && !newResource.disabled) {
                    newResource.disabled = newResource.checked;
                }

                return newResource;
            });

            resource.childrenLoading = false;
            resources = [...resources, ...newResources];

            if (!selectedId && resources.length) {
                selectedId = resources[0].id;
            }
        }
    }

    function onClose({ confirmed }) {
        if (confirmed) {
            const checkedResources = resources.filter(r => r.checked);

            // If any of the initial resources were never loaded, include them in the return since they had
            // to chance to be unchecked but were never loaded into the resources array
            initialResources.forEach(initialLO => {
                if (!resources.find(lo => initialLO.id === lo.id)) {
                    checkedResources.push(initialLO);
                }
            });

            dispatch('apply', checkedResources);
        }
    }
</script>
